import { motion } from "framer-motion";
import type { ServiceDetail } from "../data";

interface Props {
  service: ServiceDetail;
}

export default function ServiceHero({ service }: Props) {
  return (
    <section className="relative pt-0 overflow-hidden">
      {/* Hero background — OPS Solutions brand gradient */}
      <div className="relative w-full h-[520px] md:h-[620px]">
        {/* Base gradient */}
        <div className="absolute inset-0" style={{
          background: "linear-gradient(135deg, #060b16 0%, #0a1628 40%, #0f1e3a 70%, #0c1f45 100%)"
        }} />

        {/* Sky blue radial glow — top right */}
        <div className="absolute inset-0" style={{
          background: "radial-gradient(ellipse 65% 55% at 78% 30%, rgba(41,171,226,0.18) 0%, transparent 70%)"
        }} />

        {/* Deep blue glow — bottom left */}
        <div className="absolute inset-0" style={{
          background: "radial-gradient(ellipse 50% 45% at 15% 85%, rgba(37,60,125,0.35) 0%, transparent 70%)"
        }} />

        {/* Subtle grid lines */}
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M 80 0 L 0 0 0 80" fill="none" stroke="rgba(41,171,226,0.06)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* Decorative circles — mimics hero rings */}
          <circle cx="85%" cy="40%" r="200" fill="none" stroke="rgba(41,171,226,0.08)" strokeWidth="1.5"/>
          <circle cx="85%" cy="40%" r="140" fill="none" stroke="rgba(41,171,226,0.12)" strokeWidth="1"/>
          <circle cx="85%" cy="40%" r="80"  fill="none" stroke="rgba(41,171,226,0.18)" strokeWidth="1"/>
          <circle cx="85%" cy="40%" r="28"  fill="rgba(41,171,226,0.12)"/>
          <circle cx="85%" cy="40%" r="10"  fill="rgba(41,171,226,0.55)"/>
          <circle cx="85%" cy="40%" r="4"   fill="rgba(255,255,255,0.9)"/>

          {/* Floating dots */}
          <circle cx="70%" cy="20%" r="3" fill="rgba(41,171,226,0.6)"/>
          <circle cx="92%" cy="62%" r="2.5" fill="rgba(41,171,226,0.4)"/>
          <circle cx="60%" cy="70%" r="2" fill="rgba(37,60,125,0.8)"/>
          <circle cx="78%" cy="75%" r="3.5" fill="rgba(41,171,226,0.3)"/>
        </svg>

        {/* Bottom & left fade to match page bg */}
        <div className="absolute inset-0 bg-gradient-to-t from-dc-bg via-dc-bg/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-dc-bg/85 via-transparent to-transparent" />
      </div>

      {/* Content overlay */}
      <div className="absolute inset-0 flex items-end">
        <div className="max-w-7xl mx-auto px-6 md:px-10 w-full pb-16">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent/20 border border-accent/30">
                <i className={`${service.icon} text-accent text-lg`}></i>
              </div>
              <p className="text-accent text-xs font-mono tracking-widest">SERVICE</p>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white leading-[1.0] mb-4">
              {service.title}
            </h1>
            <p className="text-white/50 text-xl mb-6">{service.subtitle}</p>
            <p className="text-white/40 text-base leading-relaxed max-w-2xl mb-8">
              {service.description}
            </p>

            {/* Tagline */}
            <div className="inline-flex items-center gap-3 bg-accent/10 border border-accent/25 rounded-full px-5 py-2.5">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse inline-block"></span>
              <span className="text-accent text-sm font-medium">{service.tagline}</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 -mt-6 relative z-10 pb-16">
        <motion.div
          className="grid grid-cols-2 lg:grid-cols-4 gap-3"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          {service.stats.map((stat, i) => (
            <div
              key={i}
              className="bg-dc-card border border-dc-text/8 rounded-2xl p-5 text-center"
            >
              <p className="text-2xl font-bold text-accent font-mono">{stat.value}</p>
              <p className="text-dc-text/40 text-xs mt-1">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}